import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { AuthUser } from "./types";

// ── Cookie storage ──────────────────────────────────────────────────────────
//
// The cookie is the source of truth for the session — there is no token, so
// the whole user object (credentials included) round-trips through it.

const COOKIE_NAME = "pl_user";

function setCookie(value: AuthUser, days = 30) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(
    JSON.stringify(value)
  )}; expires=${expires}; path=/; SameSite=Strict`;
}

function getCookie(): AuthUser | null {
  const match = document.cookie
    .split("; ")
    .find((r) => r.startsWith(`${COOKIE_NAME}=`));

  if (!match) return null;

  try {
    return JSON.parse(decodeURIComponent(match.split("=").slice(1).join("=")));
  } catch {
    return null;
  }
}

function deleteCookie() {
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict`;
}

// ── Context ─────────────────────────────────────────────────────────────────

type AuthContextValue = {
  user: AuthUser | null;
  signIn: (user: AuthUser) => void;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);

  // Rehydrate the session on first mount.
  useEffect(() => {
    const saved = getCookie();
    if (saved) setUser(saved);
  }, []);

  const signIn = useCallback((userData: AuthUser) => {
    setCookie(userData);
    setUser(userData);
  }, []);

  const signOut = useCallback(() => {
    deleteCookie();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, signIn, signOut }),
    [user, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an <AuthProvider>");
  return ctx;
}
